import { useState, useEffect } from 'react';
import type { Doctor } from '@/data/featuredDoctors';
export type DoctorsSource = {
    file?: string;
    categorySlug?: string;
    all?: boolean;
    selectedSpecialty?: string;
};
const doctorModules = import.meta.glob('../../public/doctors/*.json', { eager: true }) as Record<string, {
    default?: any;
}>;
const getDoctorJson = (file: string) => {
    const normalized = file.replace(/^\/?doctors\//, '');
    const modulePath = `../../public/doctors/${normalized}`;
    const data = doctorModules[modulePath]?.default || doctorModules[modulePath];
    return data || null;
};
const getInitialDoctors = (source?: DoctorsSource): Doctor[] => {
    try {
        const indexData = getDoctorJson('index.json');
        if (source?.all) {
            return (indexData?.categories || []).flatMap((category: any) => {
                const data = getDoctorJson(category.file);
                return (data?.doctors || []).map((doctor: Doctor) => ({
                    ...doctor,
                    category: category.name,
                }));
            });
        }
        if (source?.selectedSpecialty) {
            const category = (indexData?.categories || []).find((cat: any) => cat.name === source.selectedSpecialty);
            if (!category)
                return [];
            const data = getDoctorJson(category.file);
            return (data?.doctors || []).map((doctor: Doctor) => ({
                ...doctor,
                category: category.name,
            }));
        }
        const targetFile = (() => {
            if (source?.file)
                return source.file;
            if (source?.categorySlug)
                return `/doctors/${source.categorySlug}.json`;
            return '/doctors/general.json';
        })();
        const data = getDoctorJson(targetFile);
        return (data?.doctors || []) as Doctor[];
    }
    catch {
        return [];
    }
};
export const useDoctors = (source?: DoctorsSource) => {
    const initialDoctors = getInitialDoctors(source);
    const [doctors, setDoctors] = useState<Doctor[]>(initialDoctors);
    const [loading, setLoading] = useState(initialDoctors.length === 0);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                setLoading(true);
                setError(null);
                if (source?.all) {
                    const allDoctors: Doctor[] = [];
                    const indexResponse = await fetch('/doctors/index.json');
                    if (!indexResponse.ok)
                        throw new Error('خطا در دریافت نمایه تخصص‌ها');
                    const indexData = await indexResponse.json();
                    for (const category of indexData.categories) {
                        try {
                            const response = await fetch(`/doctors/${category.file}`);
                            if (response.ok) {
                                const data = await response.json();
                                if (data.doctors && Array.isArray(data.doctors)) {
                                    const doctorsWithCategory = data.doctors.map((doctor: Doctor) => ({
                                        ...doctor,
                                        category: category.name
                                    }));
                                    allDoctors.push(...doctorsWithCategory);
                                }
                            }
                        }
                        catch (err) {
                            console.warn(`خطا در دریافت پزشکان از ${category.file}:`, err);
                        }
                    }
                    setDoctors(allDoctors);
                }
                else if (source?.selectedSpecialty) {
                    const indexResponse = await fetch('/doctors/index.json');
                    if (!indexResponse.ok)
                        throw new Error('خطا در دریافت نمایه تخصص‌ها');
                    const indexData = await indexResponse.json();
                    const category = indexData.categories.find((cat: any) => cat.name === source.selectedSpecialty);
                    if (category) {
                        const response = await fetch(`/doctors/${category.file}`);
                        if (!response.ok)
                            throw new Error(`خطا در دریافت اطلاعات پزشکان ${source.selectedSpecialty}`);
                        const data = await response.json();
                        const doctorsWithCategory = (data.doctors || []).map((doctor: Doctor) => ({
                            ...doctor,
                            category: category.name
                        }));
                        setDoctors(doctorsWithCategory);
                    }
                    else {
                        throw new Error(`تخصص '${source.selectedSpecialty}' یافت نشد`);
                    }
                }
                else {
                    const targetFile = (() => {
                        if (source?.file)
                            return source.file;
                        if (source?.categorySlug)
                            return `/doctors/${source.categorySlug}.json`;
                        return '/doctors/general.json';
                    })();
                    const response = await fetch(targetFile);
                    if (!response.ok)
                        throw new Error('خطا در دریافت اطلاعات پزشکان');
                    const data = await response.json();
                    setDoctors((data.doctors || []) as Doctor[]);
                }
            }
            catch (err) {
                console.error('خطا در دریافت لیست پزشکان:', err);
                setError(err instanceof Error ? err.message : 'خطای نامشخص');
                setDoctors([]);
            }
            finally {
                setLoading(false);
            }
        };
        fetchDoctors();
    }, [source?.file, source?.categorySlug, source?.all, source?.selectedSpecialty]);
    return { doctors, loading, error };
};
